import { useState, useEffect, useMemo, useContext } from "react";
import { message, FloatButton } from "antd";

import "./stylesRoles.css";
import { AppStrings } from "../../../utils/constants/AppStrings";
import { formatString } from "../../../utils/constants/stringUtils";
import { getApiErrorMessage, getApiSuccessMessage } from "../../../utils/ErrorUtils";
import { PlusOutlined, SaveOutlined, UndoOutlined } from "@ant-design/icons";
import NewRole from "./Actions/NewRole";
import Role from "./Actions/Role";
import SidebarPreview from "./SidebarPreview/SidebarPreview";
import { useGetPermissionsSchema, useGetRoles, useCreateRole, useUpdateRole, useDeleteRole } from "../../../queries/useRolesApi";
import { useGetStaffRainbow } from "../../../queries/useGetStaffDropdowns";
import {
  apiRoleToUiRole,
  uiRoleToCreatePayload,
  uiRoleToUpdatePayload,
  getUsedRoleColors,
  stripInvalidKeysFromPayload,
  buildDefaultPermissionStateFromSchema,
} from "../../../utils/roleUtils";
import RebetLoader from "../../CommonComponents/RebetLoader/RebetLoader";
import { AGENT_ACTIONS } from "../../../utils/Constants";
import AppContext from "../../../context/AppContext";

const NEW_ROLE_PLACEHOLDER_ID = "new";

function buildDefaultPermissionsFromSchema(schema) {
  return buildDefaultPermissionStateFromSchema(schema);
}

function buildDefaultActionsFromSchema(schema) {
  const actions = {};
  (schema?.available_actions ?? []).forEach((a) => {
    actions[a.key] = false;
  });
  return actions;
}

function Roles() {
  const { data: schema, isLoading: schemaLoading } = useGetPermissionsSchema();
  const { data: apiRoles, isLoading: rolesLoading } = useGetRoles();
  const { data: rainbowColors } = useGetStaffRainbow();
  const createRoleMutation = useCreateRole();
  const updateRoleMutation = useUpdateRole();
  const deleteRoleMutation = useDeleteRole();

  const { adminActions } = useContext(AppContext);
  
  const [selectedRole, setSelectedRole] = useState(null);
  const [isNewRoleOpen, setIsNewRoleOpen] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [localDraft, setLocalDraft] = useState(null);
  const canCreateEditPAMRole = adminActions?.includes(AGENT_ACTIONS.CREATE_EDIT_PAM_ROLE);

  const roles = useMemo(
    () => apiRoles?.map((role) => apiRoleToUiRole(role, schema)) ?? [],
    [apiRoles, schema],
  );
  const dropdownColorOptions = useMemo(() => {
    const list = Array.isArray(rainbowColors) ? rainbowColors : [];
    const seen = new Set();
    return list.filter((item) => {
      const color = item?.color || item?.value;
      if (!color) return false;
      const normalized = String(color).toLowerCase();
      if (seen.has(normalized)) return false;
      seen.add(normalized);
      return true;
    });
  }, [rainbowColors]);
  const rolesWithNew = isNewRoleOpen
    ? [
        ...roles,
        {
          id: NEW_ROLE_PLACEHOLDER_ID,
          value: NEW_ROLE_PLACEHOLDER_ID,
          label: AppStrings.NEW_ROLE,
          name: "",
          color: "#6b7280",
          permissions: buildDefaultPermissionsFromSchema(schema),
          actions: buildDefaultActionsFromSchema(schema),
          has_all_permissions: false,
          is_system_role: false,
          read_only_access: false,
          in_use: false,
          etag: null,
        },
      ]
    : roles;

  const baseSelected = rolesWithNew.find((r) => r.value === selectedRole);
  const selectedRoleData =
    baseSelected && localDraft && localDraft.value === selectedRole
      ? { ...baseSelected, ...localDraft }
      : baseSelected;
  const effectiveRoles = rolesWithNew;
  
  useEffect(() => {
    if (roles.length === 0) return;
    if (selectedRole == null || (!isNewRoleOpen && !roles.some((r) => r.id === selectedRole))) {
      setSelectedRole(roles[0]?.id ?? null);
    }
  }, [roles, isNewRoleOpen, selectedRole]);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleNewRole = () => {
    if (!canCreateEditPAMRole) return;

    setHasUnsavedChanges(false);
    setLocalDraft(null);
    setIsNewRoleOpen(true);
    setSelectedRole(NEW_ROLE_PLACEHOLDER_ID);
  };

  const applyDraft = (patch) => {
    setHasUnsavedChanges(true);
    setLocalDraft((prev) => ({
      value: selectedRole,
      ...prev,
      ...patch,
    }));
  };

  const handleChangeRoleName = (name) => {
    applyDraft({
      name: name ?? "",
      label: name === "" ? AppStrings.NEW_ROLE : (name || baseSelected?.label),
    });
  };

  const handleChangeRoleColor = (color) => {
    applyDraft({ color });
  };

  const handleActionToggle = (actionKey) => {
    const current = (selectedRoleData?.actions ?? {})[actionKey];
    applyDraft({
      actions: { ...(selectedRoleData?.actions ?? {}), [actionKey]: !current },
    });
  };

  const handleBulkActionChange = (nextActions) => {
    applyDraft({ actions: nextActions });
  };

  const handleReadOnlyAccessChange = (next) => {
    applyDraft({ read_only_access: Boolean(next) });
  };

  const handleCancelChanges = () => {
    setLocalDraft(null);
    setHasUnsavedChanges(false);
  };

  const handlePermissionsChange = (updatedPermissions) => {
    applyDraft({ permissions: updatedPermissions });
  };

  const handleSaveChanges = async () => {
    const roleData = effectiveRoles.find((r) => r.value === selectedRole);
    if (!roleData || !roleData.id || roleData.id === NEW_ROLE_PLACEHOLDER_ID) return;
    if (roleData.is_system_role) {
      message.warning(AppStrings.PAM_ROLES_SYSTEM_ROLE_NOT_MODIFIABLE);
      return;
    }
    // Use the draft version so unsaved changes are included
    const roleForPayload = selectedRoleData ?? roleData;
    const payload = uiRoleToUpdatePayload(roleForPayload, schema);
    try {
      const updated = await updateRoleMutation.mutateAsync({ roleId: roleData.id, payload });
      message.success(getApiSuccessMessage(updated, AppStrings.PAM_GENERIC_REQUEST_SUCCEEDED));
      setHasUnsavedChanges(false);
      setLocalDraft(null);
      scrollToTop();
    } catch (err) {
      const code = err?.response?.data?.error_code ?? err?.response?.data?.error?.code ?? err?.response?.data?.code;
      const msg = getApiErrorMessage(err, err?.message ?? AppStrings.PAM_GENERIC_REQUEST_FAILED);
      if (code === "SYSTEM_ROLE_PROTECTED") {
        message.error(AppStrings.PAM_ROLES_SYSTEM_ROLE_NOT_MODIFIABLE);
        return;
      }
      if (code === "ROLE_READ_ONLY") {
        // Defense-in-depth: the FE mapper already omits `actions` from the
        // payload when `read_only_access` is on, so this branch should never
        // fire in practice. The BE error message carries the specifics.
        message.error(getApiErrorMessage(err, AppStrings.PAM_GENERIC_REQUEST_FAILED));
        return;
      }
      if (code === "CONCURRENT_MODIFICATION") {
        message.error(getApiErrorMessage(err, AppStrings.PAM_GENERIC_REQUEST_FAILED));
        return;
      }
      if (code === "DUPLICATE_ROLE_NAME") {
        message.error(getApiErrorMessage(err, AppStrings.PAM_GENERIC_REQUEST_FAILED));
        return;
      }
      if (code === "INVALID_PERMISSIONS" || code === "INVALID_ACTIONS") {
        const stripped = stripInvalidKeysFromPayload(payload, err);
        const invalidPerms = err?.response?.data?.detail?.invalid_permissions ?? err?.response?.data?.invalid_permissions ?? [];
        if (invalidPerms.length > 0) {
          message.warning(
            formatString(AppStrings.PAM_ROLES_INVALID_PERMISSIONS_STRIPPED, {
              permissions: invalidPerms.join(", "),
            }),
          );
        }
        try {
          const retried = await updateRoleMutation.mutateAsync({ roleId: roleData.id, payload: stripped });
          message.success(getApiSuccessMessage(retried, AppStrings.PAM_GENERIC_REQUEST_SUCCEEDED));
          setHasUnsavedChanges(false);
          setLocalDraft(null);
          scrollToTop();
        } catch (retryErr) {
          message.error(getApiErrorMessage(retryErr, retryErr?.message ?? msg));
        }
        return;
      }
      message.error(msg);
    }
  };

  const handleSaveNewRole = async (roleName, selectedColor, permissions, actions, readOnlyAccess = false) => {
    const newRole = {
      name: roleName,
      color: selectedColor,
      permissions: permissions ?? buildDefaultPermissionsFromSchema(schema),
      actions: actions ?? buildDefaultActionsFromSchema(schema),
      has_all_permissions: false,
      read_only_access: Boolean(readOnlyAccess),
    };
    const payload = uiRoleToCreatePayload(newRole, schema);
    const usedColors = getUsedRoleColors(effectiveRoles.filter((r) => r.value !== NEW_ROLE_PLACEHOLDER_ID));
    if (usedColors.includes((selectedColor || "").toLowerCase())) {
      message.error(AppStrings.PAM_ROLES_COLOR_MUST_BE_UNIQUE);
      return;
    }
    try {
      const created = await createRoleMutation.mutateAsync(payload);
      message.success(getApiSuccessMessage(created, AppStrings.PAM_GENERIC_REQUEST_SUCCEEDED));
      setIsNewRoleOpen(false);
      setHasUnsavedChanges(false);
      setLocalDraft(null);
      setSelectedRole(created?.id ?? roles[0]?.id ?? null);
      scrollToTop();
    } catch (err) {
      const code = err?.response?.data?.error_code ?? err?.response?.data?.error?.code ?? err?.response?.data?.code;
      const msg = getApiErrorMessage(err, err?.message ?? AppStrings.PAM_GENERIC_REQUEST_FAILED);
      if (code === "DUPLICATE_ROLE_NAME") {
        message.error(getApiErrorMessage(err, AppStrings.PAM_GENERIC_REQUEST_FAILED));
        return;
      }
      if (code === "INVALID_PERMISSIONS" || code === "INVALID_ACTIONS") {
        const stripped = stripInvalidKeysFromPayload(payload, err);
        const invalidPerms = err?.response?.data?.detail?.invalid_permissions ?? err?.response?.data?.invalid_permissions ?? [];
        if (invalidPerms.length > 0) {
          message.warning(
            formatString(AppStrings.PAM_ROLES_INVALID_PERMISSIONS_STRIPPED, {
              permissions: invalidPerms.join(", "),
            }),
          );
        }
        try {
          const created = await createRoleMutation.mutateAsync(stripped);
          message.success(getApiSuccessMessage(created, AppStrings.PAM_GENERIC_REQUEST_SUCCEEDED));
          setIsNewRoleOpen(false);
          setHasUnsavedChanges(false);
          setLocalDraft(null);
          setSelectedRole(created?.id ?? roles[0]?.id ?? null);
          scrollToTop();
        } catch (retryErr) {
          message.error(getApiErrorMessage(retryErr, retryErr?.message ?? msg));
        }
        return;
      }
      message.error(msg);
    }
  };

  const handleCloseNewRole = () => {
    setIsNewRoleOpen(false);
    setHasUnsavedChanges(false);
    setLocalDraft(null);
    setSelectedRole(roles[0]?.id ?? null);
    scrollToTop();
  };

  const handleDeleteRole = async (roleId) => {
    const roleData = effectiveRoles.find((r) => r.id === roleId || r.value === roleId);
    if (roleData?.is_system_role) {
      message.warning(AppStrings.PAM_ROLES_SYSTEM_ROLE_NOT_DELETABLE);
      return;
    }
    try {
      const deleted = await deleteRoleMutation.mutateAsync(roleId);
      message.success(getApiSuccessMessage(deleted, AppStrings.PAM_GENERIC_REQUEST_SUCCEEDED));
      setHasUnsavedChanges(false);
      setLocalDraft(null);
      const remaining = effectiveRoles.filter((r) => r.id !== roleId && r.value !== roleId);
      setSelectedRole(remaining[0]?.id ?? null);
      scrollToTop();
    } catch (err) {
      const code = err?.response?.data?.error?.code ?? err?.response?.data?.code;
      const msg = getApiErrorMessage(err, err?.message ?? AppStrings.PAM_GENERIC_REQUEST_FAILED);
      if (code === "SYSTEM_ROLE_PROTECTED") message.error(AppStrings.PAM_ROLES_SYSTEM_ROLE_NOT_DELETABLE);
      else if (code === "ROLE_IN_USE") message.error(getApiErrorMessage(err, AppStrings.PAM_GENERIC_REQUEST_FAILED));
      else if (code === "ROLE_NOT_FOUND") message.error(getApiErrorMessage(err, AppStrings.PAM_GENERIC_REQUEST_FAILED));
      else message.error(msg);
    }
  };

  const usedColors = getUsedRoleColors(effectiveRoles.filter((r) => r.value !== NEW_ROLE_PLACEHOLDER_ID));

  if (schemaLoading || rolesLoading) {
    return (
      <div className="staff-management-roles-container">
        <div className="loading-container">
          <RebetLoader height="100px" width="100px" />
        </div>
      </div>
    );
  }

  return (
    <div className="staff-management-roles-container">
      <div className="staff-management-roles-left-column">
        <div className="staff-management-roles-panel staff-management-roles-panel--roles">
          <div className="staff-management-roles-container-left-header">
            <h2 className="staff-management-roles-container-left-header-title">
              {AppStrings.ROLES}
            </h2>
            {!isNewRoleOpen && (
              <div
                className={`staff-management-roles-container-left-header-button ${!canCreateEditPAMRole ? "disabled" : ""}`}
                onClick={handleNewRole}
                disabled={!canCreateEditPAMRole}
              >
                <PlusOutlined />
                {AppStrings.NEW_ROLE}
              </div>
            )}
          </div>
          <div className="staff-management-roles-container-left-body">
            {effectiveRoles.map((role) => (
              <div
                key={role.value}
                className={`staff-management-roles-container-left-body-role ${selectedRole === role.value ? "selected" : ""}`}
                onClick={() => {
                  setLocalDraft(null);
                  setHasUnsavedChanges(false);
                  setSelectedRole(role.value);
                }}
              >
                <div
                  className="staff-management-roles-container-left-body-role-color"
                  style={{ backgroundColor: role.color }}
                />
                <span className="staff-management-roles-container-left-body-role-label">
                  {role.label}
                </span>
              </div>
            ))}
          </div>
        </div>
        <div className="staff-management-roles-panel staff-management-roles-panel--sidebar-preview">
          <h3 className="sidebar-preview-title">{AppStrings.SIDEBAR_PREVIEW}</h3>
          <SidebarPreview
            key={selectedRole}
            permissions={selectedRoleData?.permissions ?? {}}
            availablePermissions={schema?.available_permissions}
          />
        </div>
      </div>
      <div className="staff-management-roles-container-right">
        {isNewRoleOpen ? (
          <NewRole
            schema={schema}
            colorOptions={dropdownColorOptions}
            usedColors={usedColors}
            handleChangeRoleName={handleChangeRoleName}
            handleChangeRoleColor={handleChangeRoleColor}
            handleSaveNewRole={handleSaveNewRole}
            handleCloseNewRole={handleCloseNewRole}
            onDraftChange={(perms, acts, readOnly) =>
              applyDraft({
                permissions: perms ?? {},
                actions: acts ?? {},
                read_only_access: Boolean(readOnly),
              })
            }
          />
        ) : (
          <>
            <Role
              key={selectedRole}
              schema={schema}
              role={selectedRoleData}
              originalColor={baseSelected?.color}
              colorOptions={dropdownColorOptions}
              handleDeleteRole={handleDeleteRole}
              onRoleNameChange={handleChangeRoleName}
              onRoleColorChange={handleChangeRoleColor}
              onActionToggle={handleActionToggle}
              onBulkActionChange={handleBulkActionChange}
              onReadOnlyAccessChange={handleReadOnlyAccessChange}
              onPermissionsChange={handlePermissionsChange}
              usedColors={usedColors}
              canCreateEditPAMRole={canCreateEditPAMRole}
            />
            {hasUnsavedChanges && selectedRoleData && !selectedRoleData.is_system_role && (
              <div className="staff-management-roles-save-container">
                <button
                  type="button"
                  className={`staff-management-roles-save-button ${!canCreateEditPAMRole ? "disabled" : ""}`}
                  onClick={handleSaveChanges}
                  disabled={updateRoleMutation.isPending || !canCreateEditPAMRole}
                >
                  {updateRoleMutation.isPending ? "Saving…" : AppStrings.SAVE_CHANGES}
                </button>
                <button
                  type="button"
                  className="staff-management-roles-cancel-button"
                  onClick={handleCancelChanges}
                  disabled={updateRoleMutation.isPending}
                >
                  {AppStrings.CANCEL_CHANGES}
                </button>
              </div>
            )}
            {hasUnsavedChanges && selectedRoleData && !selectedRoleData.is_system_role && (
              <FloatButton.Group>
                <FloatButton
                  type="primary"
                  className="staff-management-roles-float-save"
                  icon={<SaveOutlined />}
                  tooltip={{
                    title: updateRoleMutation.isPending ? "Saving…" : AppStrings.SAVE_CHANGES,
                    placement: "left",
                  }}
                  onClick={() => {
                    if (updateRoleMutation.isPending || !canCreateEditPAMRole) return;
                    handleSaveChanges();
                  }}
                />
                <FloatButton
                  className="staff-management-roles-float-cancel"
                  icon={<UndoOutlined />}
                  tooltip={{
                    title: AppStrings.CANCEL_CHANGES,
                    placement: "left",
                  }}
                  onClick={() => {
                    if (updateRoleMutation.isPending) return;
                    handleCancelChanges();
                  }}
                />
              </FloatButton.Group>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default Roles;
